import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Folder, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const LawsuitDocumentsPage = () => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => window.matchMedia('(min-width: 768px)').matches ? 3 : 1);
  const [loadState, setLoadState] = useState('loading');

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const resize = () => {
      setPageSize(media.matches ? 3 : 1);
      setPage(0);
    };
    media.addEventListener('change', resize);
    return () => media.removeEventListener('change', resize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadPosts() {
      try {
        const documents = [];
        let currentPage = 1;
        let pages = 1;
        do {
          const res = await axios.get(`${API_URL}/api/posts/list?category=lawsuit-document&limit=50&page=${currentPage}`);
          documents.push(...(res.data.posts || []));
          pages = res.data.pages || 1;
          currentPage += 1;
        } while (currentPage <= pages);
        if (!cancelled) {
          setPosts(documents.sort((a, b) => (Date.parse(b.published_at) || 0) - (Date.parse(a.published_at) || 0)));
          setLoadState('loaded');
        }
      } catch {
        if (!cancelled) setLoadState('error');
      }
    }
    loadPosts();
    return () => { cancelled = true; };
  }, []);

  const totalPages = Math.ceil(posts.length / pageSize);
  const visiblePosts = posts.slice(page * pageSize, page * pageSize + pageSize);
  const prev = () => setPage(p => Math.max(0, p - 1));
  const next = () => setPage(p => Math.max(0, Math.min(totalPages - 1, p + 1)));
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      if (event.key === 'ArrowLeft') prev();
      else next();
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="lawsuit-documents-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* Hero */}
      <div className="relative pt-[50px]">
        <div className="absolute inset-0" style={{ backgroundColor: '#9E1B32' }} />
        <div className="absolute inset-0" style={{
          backgroundImage: "url('https://securethevotemd.com/images/2025/03/2025-Feb-25-Secure-the-Vorte-66-1024x683.jpg')",
          backgroundSize: 'cover', backgroundPosition: 'center'
        }} />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.75)' }} />
        <div className="relative z-10 py-20 sm:py-28">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Lawsuit Document
            </motion.h1>
            <motion.div
              className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.3 }}
            />
          </div>
        </div>
      </div>

      <main className="py-12 sm:py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Lead-off Paragraph */}
          <motion.p
            className="text-center text-slate-600 leading-relaxed text-lg mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            On March 5th, 2024, Maryland Election Integrity LLC and United Sovereign Americans, Inc. brought a Complaint for Declaratory and Injunctive Relief against the Maryland State Board of Elections (hereinafter referred to as MDSBE). The two groups are seeking to hold the MDSBE accountable for the apparent deficient Maryland State Voter Registration Database, improper certification of voting machines, and noncompliance with the Public Information Act (PIA). The plaintiffs primary purpose for filing the lawsuit is to force these identified issues to be addressed and remedied in advance of the 2024 election.
          </motion.p>

          {/* Document Cards — 3 at a time with carousel */}
          <section className="relative" aria-label="Lawsuit documents" aria-roledescription="carousel" aria-describedby="document-browser-help" tabIndex={0} onKeyDown={handleKeyDown}>
            <p id="document-browser-help" className="sr-only">Use the previous and next buttons, or the left and right arrow keys, to browse documents.</p>
            {loadState === 'loading' && <p role="status">Loading documents…</p>}
            {loadState === 'error' && <p role="alert">Documents could not be loaded. Please refresh to try again.</p>}
            {loadState === 'loaded' && posts.length === 0 && <p>No documents available.</p>}
            <div id="lawsuit-document-cards" className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {visiblePosts.map((post, index) => {
                const date = post.published_at ? new Date(post.published_at) : null;
                return (
                  <motion.article
                    key={post.id}
                    className="bg-white rounded-lg overflow-hidden shadow-md group"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    data-testid={`lawsuit-card-${index}`}
                  >
                    {post.external_url ? (
                      <a href={post.external_url} target="_blank" rel="noopener noreferrer">
                        <div className="relative overflow-hidden">
                          {post.featured_image ? (
                            <img
                              src={post.featured_image}
                              alt={post.title}
                              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-48 bg-gradient-to-br from-[#9E1B32] to-[#6B1029] flex items-center justify-center">
                              <span className="text-white/30 text-6xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>STV</span>
                            </div>
                          )}
                          {date && (
                            <div className="absolute bottom-0 left-0 bg-[#9E1B32] text-white px-4 py-2">
                              <span className="block text-xl font-bold">
                                {date.toLocaleDateString('en-US', { day: '2-digit' })}
                              </span>
                              <span className="text-sm">
                                {date.toLocaleDateString('en-US', { month: 'short' })}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                            <Folder size={14} />
                            <span>Lawsuit Document</span>
                          </div>
                          <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#9E1B32] transition-colors mb-3 line-clamp-2">
                            {post.title}
                          </h3>
                          {post.excerpt && (
                            <p className="text-slate-600 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                          )}
                          <span className="inline-flex items-center gap-2 text-[#9E1B32] font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                            READ ARTICLE
                            <ArrowRight size={14} />
                          </span>
                        </div>
                      </a>
                    ) : (
                    <Link to={`/blog/${post.slug}`}>
                      <div className="relative overflow-hidden">
                        {post.featured_image ? (
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-48 bg-gradient-to-br from-[#9E1B32] to-[#6B1029] flex items-center justify-center">
                            <span className="text-white/30 text-6xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>STV</span>
                          </div>
                        )}
                        {date && (
                          <div className="absolute bottom-0 left-0 bg-[#9E1B32] text-white px-4 py-2">
                            <span className="block text-xl font-bold">
                              {date.toLocaleDateString('en-US', { day: '2-digit' })}
                            </span>
                            <span className="text-sm">
                              {date.toLocaleDateString('en-US', { month: 'short' })}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                          <Folder size={14} />
                          <span>Lawsuit Document</span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#9E1B32] transition-colors mb-3 line-clamp-2">
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p className="text-slate-600 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                        )}
                        <span className="inline-flex items-center gap-2 text-[#9E1B32] font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                          READ MORE
                          <ArrowRight size={14} />
                        </span>
                      </div>
                    </Link>
                    )}
                  </motion.article>
                );
              })}
            </div>

            {/* Carousel Navigation */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  type="button"
                  aria-label="Previous documents"
                  aria-controls="lawsuit-document-cards"
                  onClick={prev}
                  disabled={page === 0}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#9E1B32]"
                  style={{ backgroundColor: 'rgba(158, 27, 50, 0.85)', color: 'white' }}
                  data-testid="carousel-prev"
                >
                  <ChevronLeft size={18} />
                </button>
                <p role="status" aria-live="polite" aria-atomic="true" className="text-sm text-slate-600 text-center">
                  Documents {page * pageSize + 1}–{Math.min((page + 1) * pageSize, posts.length)} of {posts.length}
                </p>
                <button
                  type="button"
                  aria-label="Next documents"
                  aria-controls="lawsuit-document-cards"
                  onClick={next}
                  disabled={page >= totalPages - 1}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#9E1B32]"
                  style={{ backgroundColor: 'rgba(158, 27, 50, 0.85)', color: 'white' }}
                  data-testid="carousel-next"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default LawsuitDocumentsPage;

